import React, { useEffect, useState } from 'react';
import {
  fetchSecurityImageCached,
  peekSignedImageUrl,
} from '@common/services/featherIO';
import { ActivityIndicator } from 'react-native';

type Resolved = { imageUrl: string; loading: boolean };

/**
 * The state to render before any signing has happened.
 *
 * A reference we must sign resolves to no URL at all, never to `src`. Handing
 * back the stored URL first used to make every image download twice: the
 * browser fetched the stored long-lived URL, then fetched the very same object
 * again under the freshly signed one, because the two differ in their query
 * string and so miss each other in the cache. On a list that doubled the
 * requests, and the wasted half competed for the same six connections as the
 * half that was actually displayed.
 *
 * It also defeated the point of signing: the long-lived stored URL reached the
 * browser anyway, which is exactly what the short expiry exists to prevent.
 */
const initialState = (src: string | null | undefined): Resolved => {
  if (typeof src !== 'string' || !src) {
    return { imageUrl: '', loading: false };
  }
  const cached = peekSignedImageUrl(src);
  // Non-S3 sources come back unchanged, so this also covers "nothing to sign".
  if (cached) {
    return { imageUrl: cached, loading: false };
  }
  return { imageUrl: '', loading: true };
};

/**
 * Resolve any image reference into a short-lived (5-minute) pre-signed URL.
 * Accepts either an S3 keyPath ("workspaces/.../file.jpeg") or a full S3 URL
 * ("https://s3.<region>.amazonaws.com/<bucket>/<keyPath>?..."). Non-S3 sources
 * are returned unchanged.
 *
 * `imageUrl` is empty while `loading` is true. Callers that render a raw <img>
 * must not emit the tag until there is a URL: `<img src="">` makes some
 * browsers re-request the current document.
 */
export const useImageSecurity = (src: string | null | undefined) => {
  const [state, setState] = useState<Resolved>(() => initialState(src));

  useEffect(() => {
    let cancelled = false;

    const next = initialState(src);
    setState(next);
    if (!next.loading) {
      return;
    }

    fetchSecurityImageCached(src as string)
      .then((url) => {
        if (cancelled) return;
        setState({ imageUrl: url, loading: false });
      })
      .catch(() => {
        if (cancelled) return;
        // Fall back to the stored reference rather than showing nothing.
        setState({ imageUrl: src as string, loading: false });
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  return state;
};

type Props = {
  src: string | null | undefined;
  children: React.FC<{ imageUrl: string }>;
};
export const ImageSecurity: React.FC<Props> = ({ src, children }) => {
  const { imageUrl, loading } = useImageSecurity(src);

  return loading ? (
    <ActivityIndicator size="small" color="#0000ff" />
  ) : (
    children({ imageUrl })
  );
};
